import { Fragment, useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import { common } from '@mui/material/colors';
import Typography from '@mui/material/Typography';

import DataTable from 'src/components/DataTable';
import CheckButton from 'src/components/CheckButton';
import { resolveQuickSearch } from 'src/components/SearchFilterBar';
import messages from 'src/pages/ProjectDetailsPage/components/JobDetails/messages';
import ContractTooltip from 'src/pages/ProjectDetailsPage/components/ContractTooltip';
import { useStyles } from 'src/pages/ProjectDetailsPage/components/JobDetails/JobDetails.styled';
import TransferForm from 'src/pages/ProjectDetailsPage/components/JobDetails/components/TransferForm';
import { useGetBookedToTransfer } from 'src/pages/ProjectDetailsPage/api/queries/useGetBookedToTransfer';

import type { DefaultTheme } from '@mui/styles/defaultTheme';
import { map } from 'lodash';

const toTransferColumnLabels = {
  no_answer: 'No answer',
  false: 'No',
  true: 'Yes',
};

export default function JobAvailable({
  jobId,
  filters,
  candidate,
  standbyUuid = '',
  onCandidateSelect,
}: UCM.JobStandbyTabProps) {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const [selected, setSelected] = useState<Array<UCM.ApplicationType>>([]);

  const { data: jobApplications, isLoading } = useGetBookedToTransfer({
    standbyUuid,
    jobId,
    enabled: Boolean(standbyUuid) && Boolean(jobId),
    filters: resolveQuickSearch(filters),
  });

  const checkIfExist = useCallback(
    (uuid: string) => {
      return selected.findIndex((item) => item.uuid === uuid) !== -1;
    },
    [selected.length],
  );

  const isSelectAll = useMemo(() => {
    return (
      jobApplications?.length !== 0 &&
      selected.length === jobApplications?.length
    );
  }, [selected.length, jobApplications?.length]);

  const handleSelectAll = useCallback(() => {
    if (isSelectAll) {
      setSelected([]);
    } else if (jobApplications) {
      setSelected(jobApplications.map((application) => application.applicant));
    }
  }, [jobApplications?.length, isSelectAll]);

  const handleSelect = useCallback(
    (uuid: string) => {
      const exist = checkIfExist(uuid);
      let local = [...selected];
      if (exist) {
        const filtered = local.filter((item) => item.uuid !== uuid);
        local = filtered;
      } else {
        const application = jobApplications?.find(
          (item) => item.applicant.uuid === uuid,
        );
        if (application) {
          local.push(application.applicant);
        }
      }
      setSelected(local);
    },
    [selected.length, checkIfExist, jobApplications?.length],
  );

  const handleUserUnselect = useCallback(
    (user: UCM.ApplicationType) => {
      const updatedSelected = selected.filter(
        (item) => item.uuid !== user.uuid,
      );
      setSelected(updatedSelected);
    },
    [selected],
  );

  const columns: Array<{
    id: keyof UCM.JobApplicationType | string;
    label: string | React.ReactNode;
  }> = useMemo(
    () => [
      {
        id: 'applicant',
        label: (
          <Stack gap={1} flexDirection="row" alignItems="center">
            <CheckButton active={isSelectAll} onChange={handleSelectAll} />
            <Typography
              variant="inherit"
              fontWeight={(theme) => theme.typography.fontWeightMedium}
            >
              {formatMessage(messages.applicantLabel)}
            </Typography>
          </Stack>
        ),
        render: (_: Object, item: UCM.JobApplicationType) => {
          const { id, fullName, uuid } = item.applicant;
          const isSelected = checkIfExist(uuid);

          return (
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <CheckButton
                active={isSelected}
                onChange={() => handleSelect(uuid)}
              />
              <Box>
                <Typography
                  variant="inherit"
                  fontWeight={(theme) => theme.typography.fontWeightMedium}
                >
                  {`${id} - ${fullName}`}
                </Typography>
              </Box>
            </Stack>
          );
        },
      },
      {
        id: 'appliedCount',
        align: 'center',
        label: formatMessage(messages.appliedToLabel),
        render: (appliedCount: number) => (
          <Typography variant="inherit">{appliedCount ?? '-'}</Typography>
        ),
      },
      // {
      //   id: 'workday',
      //   align: 'center',
      //   label: formatMessage(messages.workdayLabel),
      //   // TODO - waiting for backend adjustements
      //   render: () => <Typography variant="inherit">DD/MM/YYYY</Typography>,
      // },
      {
        id: 'ratingScore',
        align: 'center',
        label: formatMessage(messages.ratingLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.ratingScore) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.ratingScore}
            </Typography>
          );
        },
      },
      // TODO - waiting for backend adjustements
      // {
      //   id: 'preScreen',
      //   align: 'center',
      //   label: formatMessage(messages.preScreenLabel),
      //   render: (_: Object, item: UCM.JobApplicationType) => {
      //     const { prescreeningRating } = item.applicant;

      //     return (
      //       <Tooltip
      //         arrow
      //         title={
      //           <Stack spacing={1}>
      //             {Object.entries(prescreeningRating).map(([key, value]) => (
      //               <Typography
      //                 key={key}
      //                 variant="inherit"
      //                 textTransform="capitalize"
      //               >
      //                 {key}: {value}
      //               </Typography>
      //             ))}
      //           </Stack>
      //         }
      //       >
      //         <Typography variant="inherit">
      //           {prescreeningRating.average}
      //         </Typography>
      //       </Tooltip>
      //     );
      //   },
      // },
      {
        id: 'profile',
        align: 'center',
        label: formatMessage(messages.profileLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant?.status) return '-';
          let label = formatMessage(messages.completeLabel);
          if (item.applicant?.status?.code === 'required')
            label = formatMessage(messages.incompleteLabel);
          return <Typography variant="inherit">{label}</Typography>;
        },
      },
      {
        id: 'toTransfer',
        align: 'center',
        label: formatMessage(messages.toTransferLabel),
        render: renderToTransferColumn,
      },
      {
        id: 'contractSummary',
        align: 'center',
        label: formatMessage(messages.contractSummaryLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.contractSummary.status) return '-';
          return <ContractTooltip contract={item?.contractSummary} />;
        },
      },
    ],
    [isSelectAll, handleSelectAll, handleSelect],
  );

  const tableFooterSx = useCallback(
    (theme: DefaultTheme) => ({
      borderTop: `thin solid ${theme.palette.grey[200]}`,
    }),
    [],
  );

  return (
    <Grid size={{ xs: 12 }}>
      <DataTable
        columns={columns}
        loading={isLoading}
        enabledFooter={false}
        activeRow={candidate}
        rows={jobApplications}
        checkActiveKey="applicant.id"
        pressRow={onCandidateSelect}
        className={classes.tableContainer}
      />

      <Stack
        p={2}
        sx={tableFooterSx}
        alignItems="flex-end"
        bgcolor={common.white}
      >
        <TransferForm
          selectedUsers={selected}
          standbyJobUuid={standbyUuid}
          onUserUnselect={handleUserUnselect}
        />
      </Stack>
    </Grid>
  );
}

function renderToTransferColumn(
  toTransfer: UCM.JobApplicationType['toTransfer'],
) {
  if (!toTransfer) return '-';

  const keys = Object.keys(toTransfer) as Array<
    keyof UCM.JobApplicationType['toTransfer']
  >;

  if (keys.length === 1 && keys[0] === 'no_answer') {
    return '-';
  }

  const hideCount = keys.length === 1 && toTransfer[keys[0]] < 2;

  return map(
    toTransfer,
    (value: number, key: keyof typeof toTransferColumnLabels) => {
      const label = toTransferColumnLabels[key] || key;

      return (
        <Fragment key={key}>
          <Typography variant="inherit">
            {hideCount ? label : `${label}: ${value}`}
          </Typography>
          <br />
        </Fragment>
      );
    },
  );
}
